import { useState } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { PauseCircle, Loader2 } from "lucide-react";

const DURATIONS = [
  { days: 30, label: "30 days", hint: "A short break — comes back next month." },
  { days: 60, label: "60 days", hint: "Common for between-deals stretches." },
  { days: 90, label: "90 days", hint: "A full quarter — max duration." },
];

export default function PauseSubscriptionModal({ open, onClose, onPaused }) {
  const [duration, setDuration] = useState(30);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/subscription/pause", {
        duration_days: duration,
        reason: reason.trim() || undefined,
      });
      const resumesAt = data?.subscription_resumes_at
        ? new Date(data.subscription_resumes_at).toLocaleDateString([], { month: "long", day: "numeric", year: "numeric" })
        : null;
      toast.success(resumesAt ? `Paused until ${resumesAt}.` : "Subscription paused.");
      setReason("");
      setDuration(30);
      if (onPaused) await onPaused();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to pause subscription.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && !busy) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="pause-subscription-modal">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "Outfit" }} className="flex items-center gap-2">
            <PauseCircle className="w-5 h-5 text-amber-600" />
            Pause subscription
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 text-sm text-slate-700">
          <p className="text-slate-600">
            Take a short break instead of cancelling. Your portfolio stays in read-only mode and we won't charge you again until you resume.
          </p>

          <div>
            <div className="overline text-slate-500 mb-2">Duration</div>
            <div className="grid grid-cols-3 gap-2">
              {DURATIONS.map((d) => (
                <button
                  key={d.days}
                  type="button"
                  onClick={() => setDuration(d.days)}
                  data-testid={`pause-duration-${d.days}`}
                  className={`border rounded-sm px-3 py-2 text-center transition-colors ${
                    duration === d.days
                      ? "border-amber-500 bg-amber-50 text-amber-900"
                      : "border-slate-200 bg-white text-slate-700 hover:border-slate-400"
                  }`}
                >
                  <div className="font-semibold text-sm">{d.label}</div>
                </button>
              ))}
            </div>
            <div className="text-[11px] text-slate-500 mt-1.5">
              {DURATIONS.find((d) => d.days === duration)?.hint}
            </div>
          </div>

          <div>
            <div className="overline text-slate-500 mb-1">Reason (optional)</div>
            <textarea
              rows={3}
              maxLength={300}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              data-testid="pause-reason"
              placeholder="e.g. Between deals, traveling, snowbird season."
              className="input-clean w-full text-sm"
            />
            <div className="text-[11px] text-slate-400 text-right mt-0.5 font-mono">{reason.length}/300</div>
          </div>
        </div>

        <DialogFooter>
          <button
            type="button"
            className="btn-outline-dark"
            onClick={onClose}
            disabled={busy}
            data-testid="pause-cancel"
          >
            Never mind
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={busy}
            data-testid="pause-confirm"
            className="inline-flex items-center gap-2 bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold px-4 py-2 rounded-sm disabled:opacity-60"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <PauseCircle className="w-4 h-4" />}
            Pause for {duration} days
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
